import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, Drawer, useToast, Notice,
} from '@kollez/design-system';
import {
  useExam, useStartAttempt, useAttempt, useSubmitAttemptAnswer, useFinalizeAttempt,
  useAttemptQuestion, type AttemptQuestion,
} from '@/lib/queries';

/**
 * /assess/take/:examId (F74) — candidate take flow with real widgets per Kollez design.
 * Layout mirrors kollez/pages/assess/take.html:
 *   - Sticky header with timer + question map + progress bar
 *   - One question at a time
 *   - Widgets per type (single_choice/multi_choice/true_false/numeric/short/code)
 *   - Question-map drawer for jumping around
 *   - Auto-save on submit; finalize at the end
 */
export function AssessTakePage() {
  const { examId = '' } = useParams();
  const nav = useNavigate();
  const exam = useExam(examId);
  const start = useStartAttempt();
  const toast = useToast();
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [mapOpen, setMapOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function begin() {
      if (!examId || attemptId) return;
      try {
        const r = await start.mutateAsync(examId);
        if (cancelled) return;
        // F-100: server-side idempotent start now returns the existing attempt with
        // `alreadySubmitted: true`. Redirect to results without hitting an error path.
        if ((r as { alreadySubmitted?: boolean }).alreadySubmitted) {
          nav(`/assess/exams/${examId}/results`, { replace: true });
          return;
        }
        setAttemptId(r.attempt._id);
      } catch (err) {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Could not start the attempt';
        // F-093: fallback for older servers still returning 400 "already submitted".
        if (/already submitted/i.test(msg)) {
          nav(`/assess/exams/${examId}/results`, { replace: true });
          return;
        }
        toast.show({ tone: 'warning', message: msg });
      }
    }
    begin();
    return () => { cancelled = true; };
  }, [examId, attemptId]);

  const attempt = useAttempt(attemptId ?? undefined);
  const submit = useSubmitAttemptAnswer(attemptId ?? '');
  const finalize = useFinalizeAttempt(attemptId ?? '');

  const materialized = attempt.data?.attempt.materializedQuestions ?? [];
  const answersByQ = useMemo(() => {
    const m: Record<string, { value: unknown; score?: number }> = {};
    for (const a of attempt.data?.attempt.answers ?? []) m[String(a.question)] = { value: a.value, score: a.score };
    return m;
  }, [attempt.data?.attempt.answers]);

  const answeredCount = Object.values(answersByQ).length;
  const currentQid = materialized[idx]?.questionId;
  const isDone = attempt.data?.attempt.status === 'submitted' || attempt.data?.attempt.status === 'under_review';

  // Timer — window.closesAt is authoritative; we show client-side countdown.
  const closesAt = exam.data?.assessment.window.closesAt;
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const secLeft = closesAt ? Math.max(0, Math.floor((new Date(closesAt).getTime() - now) / 1000)) : 0;
  const mm = Math.floor(secLeft / 60);
  const ss = secLeft % 60;
  const low = secLeft > 0 && secLeft < 5 * 60;

  async function saveAnswer(value: unknown) {
    if (!currentQid) return;
    try {
      await submit.mutateAsync({ questionId: currentQid, value });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? 'Save failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }
  async function saveAndNext(value: unknown) {
    await saveAnswer(value);
    if (idx < materialized.length - 1) setIdx(idx + 1);
  }
  async function finishAttempt() {
    try {
      const r = await finalize.mutateAsync();
      toast.show({ message: `Submitted · ${r.attempt.totalScore ?? 0} marks${r.attempt.pendingReview ? ` (${r.attempt.pendingReview} pending review)` : ''}` });
    } catch {
      toast.show({ tone: 'warning', message: 'Submit failed.' });
    }
  }

  return (
    <AsyncBoundary
      kind="detail"
      loading={exam.isLoading || attempt.isLoading}
      error={exam.error ?? attempt.error}
      empty={!exam.isLoading && !exam.data}
      emptyTitle="Exam not found"
      emptyHint="This exam may have closed or the link is stale. Head back to your Tests list."
      onRetry={() => { void exam.refetch(); if (attemptId) void attempt.refetch(); }}
    >
      {exam.data && (
        <div className="stack">
          {/* Sticky exam header */}
          <Card padding="sm" className="sticky top-0 z-30 bg-bg-primary/95 backdrop-blur">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{exam.data.assessment.name}</div>
                <div className="text-xs text-ink-tertiary">Question {Math.min(idx + 1, materialized.length)} of {materialized.length} · {answeredCount} answered</div>
              </div>
              <Button variant="ghost" onClick={() => setMapOpen(true)}>Questions</Button>
              {closesAt && !isDone && (
                <span className={`px-3 py-1 rounded text-sm font-semibold ${low ? 'bg-bg-danger text-ink-danger' : 'bg-bg-tertiary'}`} role="timer">
                  {mm}:{String(ss).padStart(2, '0')}
                </span>
              )}
              {!isDone && attempt.data && (
                <Button variant="primary" onClick={finishAttempt} loading={finalize.isPending}>Submit</Button>
              )}
            </div>
            <div className="mt-2 h-[3px] bg-bg-tertiary rounded overflow-hidden">
              <span className="block h-full bg-ink-accent transition-all" style={{ width: `${Math.round((answeredCount / Math.max(1, materialized.length)) * 100)}%` }} />
            </div>
          </Card>

          {isDone ? (
            <Card padding="lg" className="justify-items-start">
              <Badge tone="success">Submitted</Badge>
              <div className="flex items-baseline gap-3 mt-3">
                <span className="font-serif text-5xl">{attempt.data?.attempt.totalScore ?? 0}</span>
                <span className="text-ink-tertiary">/ {exam.data.assessment.scoring.totalMarks}</span>
              </div>
              <p className="text-ink-secondary mt-2">
                {attempt.data?.attempt.status === 'under_review'
                  ? `${attempt.data.attempt.pendingReview} question(s) need human review. Your final score will update once graded.`
                  : 'All questions auto-graded. Your final score is above.'}
              </p>
            </Card>
          ) : currentQid ? (
            <QuestionRunner
              attemptId={attemptId ?? ''}
              questionId={currentQid}
              index={idx}
              total={materialized.length}
              savedValue={answersByQ[currentQid]?.value}
              alreadyGraded={typeof answersByQ[currentQid]?.score === 'number' || answersByQ[currentQid] !== undefined}
              pending={submit.isPending}
              onSaveAndNext={saveAndNext}
              onSave={saveAnswer}
            />
          ) : null}

          {/* Navigation */}
          {!isDone && (
            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}>← Previous</Button>
              <Button variant="ghost" onClick={() => setIdx(Math.min(materialized.length - 1, idx + 1))} disabled={idx >= materialized.length - 1}>Next →</Button>
            </div>
          )}

          {/* Question map drawer */}
          <Drawer open={mapOpen} onClose={() => setMapOpen(false)} title="Questions" size="sm">
            <div className="grid grid-cols-8 gap-1.5">
              {materialized.map((_, i) => {
                const qid = materialized[i]?.questionId ?? '';
                const answered = answersByQ[qid] !== undefined;
                return (
                  <button
                    key={i}
                    onClick={() => { setIdx(i); setMapOpen(false); }}
                    className={`aspect-square rounded text-xs border ${
                      i === idx ? 'ring-2 ring-ink-accent border-ink-accent'
                      : answered ? 'bg-bg-info/40 border-ink-accent'
                      : 'bg-bg-primary border-line-secondary'
                    }`}
                  >
                    {i + 1}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 text-xs text-ink-tertiary mt-3">
              <span><span className="inline-block w-2 h-2 rounded-full bg-ink-accent mr-1" />Answered</span>
              <span><span className="inline-block w-2 h-2 rounded-full bg-line-secondary mr-1" />Not yet</span>
            </div>
          </Drawer>
        </div>
      )}
    </AsyncBoundary>
  );
}

function QuestionRunner({
  attemptId, questionId, index, total, savedValue, alreadyGraded, pending, onSaveAndNext, onSave,
}: {
  attemptId: string;
  questionId: string;
  index: number;
  total: number;
  savedValue: unknown;
  alreadyGraded: boolean;
  pending: boolean;
  onSaveAndNext: (v: unknown) => Promise<void>;
  onSave: (v: unknown) => Promise<void>;
}) {
  const { data, isLoading, error, refetch } = useAttemptQuestion(attemptId, questionId);
  const q = data?.question;
  const [value, setValue] = useState<unknown>(savedValue ?? null);
  const initedFor = useRef<string | null>(null);

  useEffect(() => {
    if (initedFor.current !== questionId) {
      setValue(savedValue ?? null);
      initedFor.current = questionId;
    }
  }, [questionId, savedValue]);

  return (
    <AsyncBoundary kind="detail" loading={isLoading} error={error} onRetry={() => void refetch()}>
      {q && (
        <Card padding="lg">
          <div className="text-xs text-ink-tertiary uppercase tracking-wide flex items-center gap-2 mb-2">
            <span>Question {index + 1} of {total}</span>
            <span>·</span>
            <span>{q.marks} marks</span>
            {q.difficulty && <Badge tone="outline">{q.difficulty}</Badge>}
            {alreadyGraded && <Badge tone="info">Answered — you can change it</Badge>}
          </div>
          <div className="text-[16px] leading-relaxed mb-4 whitespace-pre-wrap">{q.stem}</div>

          <QuestionWidget question={q} value={value} onChange={setValue} />

          <div className="flex flex-wrap gap-2 mt-4">
            <Button variant="primary" onClick={() => onSaveAndNext(value)} loading={pending}>
              {index === total - 1 ? 'Save answer' : 'Save & next'}
            </Button>
            <Button variant="ghost" onClick={() => onSave(value)} disabled={pending}>Save (stay here)</Button>
          </div>
          {q.wrongPenalty ? (
            <Notice tone="warning" className="mt-3">
              Wrong choice loses {q.wrongPenalty} mark(s). Skip if unsure.
            </Notice>
          ) : null}
        </Card>
      )}
    </AsyncBoundary>
  );
}

function QuestionWidget({ question, value, onChange }: { question: AttemptQuestion; value: unknown; onChange: (v: unknown) => void }) {
  const letters = 'ABCDEFGH';
  const t = question.type;

  if (t === 'single_choice') {
    const chosen = String(value ?? '');
    return (
      <div className="grid gap-2" role="radiogroup">
        {(question.options ?? []).map((o, j) => (
          <button
            key={j}
            type="button"
            role="radio"
            aria-checked={chosen === o.text}
            onClick={() => onChange(o.text)}
            className={`text-left p-3 rounded border flex items-start gap-3 transition ${
              chosen === o.text ? 'border-ink-accent bg-bg-info/20' : 'border-line-secondary hover:bg-bg-secondary/40'
            }`}
          >
            <span className="font-semibold text-ink-tertiary">{letters[j]}</span>
            <span>{o.text}</span>
          </button>
        ))}
      </div>
    );
  }

  if (t === 'multi_choice') {
    const chosen = new Set(Array.isArray(value) ? (value as string[]) : []);
    function toggle(text: string) {
      const next = new Set(chosen);
      if (next.has(text)) next.delete(text); else next.add(text);
      onChange([...next]);
    }
    return (
      <>
        <div className="grid gap-2" role="group">
          {(question.options ?? []).map((o, j) => (
            <button
              key={j}
              type="button"
              role="checkbox"
              aria-checked={chosen.has(o.text)}
              onClick={() => toggle(o.text)}
              className={`text-left p-3 rounded border flex items-start gap-3 transition ${
                chosen.has(o.text) ? 'border-ink-accent bg-bg-info/20' : 'border-line-secondary hover:bg-bg-secondary/40'
              }`}
            >
              <span className="font-semibold text-ink-tertiary">{letters[j]}</span>
              <span>{o.text}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-tertiary mt-2">Select all that apply. Partial credit is not given.</p>
      </>
    );
  }

  if (t === 'true_false') {
    const v = String(value ?? '');
    return (
      <div className="grid grid-cols-2 gap-2" role="radiogroup">
        {['True', 'False'].map((label) => (
          <button
            key={label}
            type="button"
            role="radio"
            aria-checked={v === label}
            onClick={() => onChange(label)}
            className={`p-3 rounded border font-medium ${
              v === label ? 'border-ink-accent bg-bg-info/20' : 'border-line-secondary hover:bg-bg-secondary/40'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  if (t === 'numeric') {
    return (
      <input
        type="number"
        inputMode="decimal"
        value={typeof value === 'number' || typeof value === 'string' ? String(value) : ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="input max-w-[260px]"
        placeholder="Your answer"
      />
    );
  }

  if (t === 'code') {
    return (
      <textarea
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        rows={12}
        className="input w-full font-mono text-sm"
        placeholder="Write your solution here"
        spellCheck={false}
      />
    );
  }

  // short_answer / long_answer / essay / fill_blank / any other type → free text
  return (
    <textarea
      value={String(value ?? '')}
      onChange={(e) => onChange(e.target.value)}
      rows={5}
      className="input w-full"
      placeholder="Your answer"
    />
  );
}
